import { useEffect, useMemo, useState } from 'react';

//interfaces
import {IWhitelistTerm, NotificationStatus} from '../../../../../interfaces/languages';
import { IconLibrary, ISimpleTableProps, ISimpleTableRows, ThriveIcon } from 'thrive-components';

//components
import TermTableInformation from './TermTableInformation';
import { useTimer } from '@/components/hooks/utility/useTimer';
import { createNewExclusion, listAllExclusions, removeExclusion, updateExclusion } from '@/api/orgSettings/languages';



export const UseDoNotTranslateTab = () => {
    const [isLoading, setIsLoading]=useState<boolean>(true);
    const tableColumns = [{title: "Term", isToggleButton: false}];
    const [termData, setTermData] = useState<IWhitelistTerm[]>([]);
    const [editTermItem, setEditTermItem] = useState<IWhitelistTerm | null>(null);
    const [removeTermItem, setRemoveTermItem] = useState<IWhitelistTerm | null>(null);
    const [showAddTermModal, setShowAddTermModal] = useState<boolean>(false);
    const [notificationDetails, setNotificationDetails] = useState<{show: boolean, status: NotificationStatus, message: string}>({show: false, status: NotificationStatus.null, message: ""});

    const termTableRows = useMemo(() => {
        return createTermTableRows();
    }, [termData]);

    const termTableContent: ISimpleTableProps = {
        simpleTableContent: { columns: tableColumns, rows: termTableRows },
        isLoading,
        isInfiniteScroll: false,
        noDataMessage: "No items to display",
        hideActionsColumn: false,
    };

    useTimer(notificationDetails?.show ?? false, ()=>setNotificationDetails({show: false, status:NotificationStatus.null, message: ""}), 3000);

    useEffect(() => {
        getTerms();
    }, []);

    function createTermTableRows() {        
        return termData.map((term): ISimpleTableRows => ({
            content: [<TermTableInformation termInformation={term} />],
            controls: [
                {
                    content:
                        <span className="translation-menu-button">
                            <ThriveIcon iconName={IconLibrary.PENCIL} sizeInPx='12' color='var(--thriveCMSBlue)' />
                            Edit
                        </span>, 
                    onClick: () => setEditTermItem(term)
                },
                {   content:
                        <span className="translation-menu-button">
                            <ThriveIcon iconName={IconLibrary.BIN} sizeInPx='12' color='var(--thriveRed)' />
                            Remove
                        </span>,  
                    onClick: () => setRemoveTermItem(term)
                }
            ]
        }));  
    }

    function onCloseAddTermModal(){
        setEditTermItem(null);
        setShowAddTermModal(false);
    }

    async function getTerms(): Promise<void> {
        setIsLoading(true);

        listAllExclusions()
        .then((data) => {
            setTermData(data);
        })
        .catch((error: any) => {
            const status = error?.response?.status;

            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message:
                    status === 500
                        ? "Something went wrong when fetching terms"
                        : (error?.response?.data || "Something went wrong")
            });
        })
        .finally(() => {
            setIsLoading(false);
        });
    }

    function onAddNewTermClick(term: IWhitelistTerm){
        setIsLoading(true);

        createNewExclusion({text: term.text, description: term.description})
        .then(()=> {
            setNotificationDetails({
                show: true,
                status: NotificationStatus.success,
                message: "Term added. It will remain unchanged in future translations."
            });
            
            setShowAddTermModal(false);
        })
        .catch((error: any) => {
            const status = error?.response?.status;

            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message:
                    status === 500
                        ? "Something went wrong when adding new term"
                        : (error?.response?.data || "Something went wrong")
            });
        })
        .finally(() =>   {
            getTerms();
        });
    }

    function onEditTermClick(term: IWhitelistTerm) {
        if (!editTermItem) return;
        setIsLoading(true);

        updateExclusion(editTermItem.id, {text: term.text, description: term.description ?? ""})
        .then(()=> {
            setNotificationDetails({
                show: true,
                status: NotificationStatus.success,
                message: "Term updated. It will remain unchanged in future translations."
            });
            setEditTermItem(null);
        })
        .catch((error: any) => {
            const status = error?.response?.status;

            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message:
                    status === 500
                        ? "Something went wrong when updating new term"
                        : (error?.response?.data || "Something went wrong")
            });
        })
        .finally(() =>   {
            getTerms();
        })
    }

    function onRemoveTerm(id?: number) {     
        if (!id) return;
        
        removeExclusion(id)
        .then(()=> {
            setNotificationDetails({
                show: true,
                status: NotificationStatus.success,
                message: "Term removed. It will no longer be excluded from translation."
            });
            setRemoveTermItem(null);
        })
        .catch((error: any) => {
            const status = error?.response?.status;

            setNotificationDetails({
                show: true,
                status: NotificationStatus.error,
                message:
                    status === 500
                        ? "Something went wrong when updating term"
                        : (error?.response?.data || "Something went wrong")
            });
        })
        .finally(() =>   {
            getTerms();
        })
    }

    return {
        termTableContent,
        isLoading,
        setRemoveTermItem,
        removeTermItem,
        editTermItem,
        showAddTermModal,
        setShowAddTermModal,
        notificationDetails,
        onCloseAddTermModal,
        onRemoveTerm,
        onEditTermClick,
        onAddNewTermClick,
    }
};